import { useMutation } from '@apollo/client';
import type { InstatiateEnterpriseRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InstatiateEnterpriseRiskDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InstantiateEnterpriseRiskInput } from './useInstantiateEnterpriseRiskTRPC';
import { useInstantiateEnterpriseRiskTRPC } from './useInstantiateEnterpriseRiskTRPC';

/**
 * Flag-gated wrapper (RSP-6498). Delegates to the tRPC twin when the `trpc`
 * flag is on, else the legacy Apollo Hasura-action path. Consumers import THIS,
 * never the *TRPC twin.
 */
export const useInstantiateEnterpriseRisk = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpcMutation = useInstantiateEnterpriseRiskTRPC();

  const [instantiateGraphQL, graphqlState] = useMutation(
    InstatiateEnterpriseRiskDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk');
      },
    }
  );

  const instantiateEnterpriseRisk = async (
    variables: InstantiateEnterpriseRiskInput
  ): Promise<InstatiateEnterpriseRiskMutation | undefined> => {
    if (trpcEnabled) {
      return trpcMutation.instantiateEnterpriseRisk(variables);
    }

    const result = await instantiateGraphQL({ variables });

    return result.data ?? undefined;
  };

  if (trpcEnabled) {
    return {
      instantiateEnterpriseRisk,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    instantiateEnterpriseRisk,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
